import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Play, BookOpen } from 'lucide-react';
import { Button } from './ui/button';

const TutorialsModalPortal = ({ isOpen, onClose }) => {
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTutorial, setSelectedTutorial] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    if (isOpen) {
      loadTutorials();
    }
  }, [isOpen]);

  const loadTutorials = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tutorials`);
      
      if (response.ok) {
        const data = await response.json();
        // Sort by order number to ensure correct display order
        const sortedData = [...data].sort((a, b) => {
          const orderA = parseInt(a.order, 10) || 0;
          const orderB = parseInt(b.order, 10) || 0;
          return orderA - orderB;
        });
        setTutorials(sortedData);
      } else {
        setError('Failed to load tutorials');
      }
    } catch (err) {
      console.error('Error loading tutorials:', err);
      setError('Failed to load tutorials');
    } finally {
      setLoading(false);
    }
  };

  const getYouTubeThumbnail = (embedUrl) => {
    if (!embedUrl || typeof embedUrl !== 'string') return null;
    try {
      const videoId = embedUrl.match(/embed\/([^?]+)/)?.[1];
      return videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null;
    } catch (err) {
      console.error('Error extracting thumbnail:', err);
      return null;
    }
  };

  const categories = ['all', ...new Set(tutorials.map(t => t.category).filter(Boolean))];
  const filteredTutorials = selectedCategory === 'all' 
    ? tutorials 
    : tutorials.filter(t => t.category === selectedCategory);

  if (!isOpen) return null;

  const modalContent = (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 999999,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '1200px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          zIndex: 1000000,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ 
          padding: '20px 24px', 
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <BookOpen style={{ width: '24px', height: '24px', color: '#3b82f6' }} />
            <h2 style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>Training Tutorials</h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X style={{ width: '20px', height: '20px' }} />
          </button>
        </div>

        {/* Content */}
        <div style={{ 
          flex: 1, 
          overflowY: 'auto',
          padding: '20px 24px'
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ 
                display: 'inline-block',
                width: '40px',
                height: '40px',
                border: '4px solid #e5e7eb',
                borderTopColor: '#3b82f6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ marginTop: '16px', color: '#6b7280' }}>Loading tutorials...</p>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p style={{ color: '#ef4444', marginBottom: '16px' }}>{error}</p>
              <Button onClick={loadTutorials}>Try Again</Button>
            </div>
          ) : (
            <>
              {/* Category Filter */}
              <div style={{ marginBottom: '20px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    style={{
                      padding: '6px 16px',
                      borderRadius: '20px',
                      border: selectedCategory === category ? '2px solid #3b82f6' : '1px solid #d1d5db',
                      backgroundColor: selectedCategory === category ? '#eff6ff' : 'white',
                      color: selectedCategory === category ? '#3b82f6' : '#6b7280',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: selectedCategory === category ? '600' : '400',
                      textTransform: 'capitalize'
                    }}
                  >
                    {category}
                  </button>
                ))}
              </div>

              {/* Tutorial Grid */}
              {selectedTutorial ? (
                <div>
                  <button
                    onClick={() => setSelectedTutorial(null)}
                    style={{
                      marginBottom: '16px',
                      padding: '8px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      background: 'white',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    ← Back to tutorials
                  </button>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px' }}>
                    {selectedTutorial.title}
                  </h3>
                  {selectedTutorial.description && (
                    <p style={{ color: '#6b7280', marginBottom: '16px', fontSize: '14px' }}>
                      {selectedTutorial.description}
                    </p>
                  )}
                  {!selectedTutorial.embed_url && (selectedTutorial.steps || []).length > 0 ? (
                    <div data-testid="tutorial-written-guide" style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                      {selectedTutorial.steps.map((st, i) => (
                        <div key={i} data-testid={`tutorial-step-${i + 1}`} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '14px', background: 'white' }}>
                          <p style={{ fontSize: '15px', fontWeight: 500, color: '#1e293b', marginBottom: '10px', display: 'flex', gap: '10px' }}>
                            <span style={{ minWidth: '24px', height: '24px', borderRadius: '12px', background: '#0586ba', color: 'white', fontSize: '12px', fontWeight: 700,
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
                            {st.text}
                          </p>
                          {st.image && <img src={st.image} alt={`Step ${i + 1}`} loading="lazy" style={{ maxWidth: '100%', borderRadius: '6px', border: '1px solid #e2e8f0' }} />}
                        </div>
                      ))}
                    </div>
                  ) : (
                  <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                    <iframe
                      src={selectedTutorial.embed_url}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        borderRadius: '8px'
                      }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                  )}
                </div>
              ) : (
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '16px'
                }}>
                  {filteredTutorials.map(tutorial => (
                    <div
                      key={tutorial.id}
                      style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        backgroundColor: 'white'
                      }}
                    >
                      {/* YouTube Video Embed - exactly like admin */}
                      <div style={{ position: 'relative', backgroundColor: '#000', borderRadius: '8px 8px 0 0', overflow: 'hidden' }}>
                        {/\.mp4(\?|$)/i.test(tutorial.embed_url || '') ? (
                          <video src={tutorial.embed_url} poster={tutorial.embed_url.replace(/\.mp4.*$/i, '.jpg')} controls playsInline preload="metadata"
                            style={{ width: '100%', aspectRatio: '16/9', display: 'block', background: '#000' }} data-testid={`tutorial-video-${tutorial.id}`} />
                        ) : tutorial.embed_url ? (
                          <iframe
                            src={tutorial.embed_url}
                            title={tutorial.title}
                            style={{
                              width: '100%',
                              aspectRatio: '16/9',
                              border: 'none'
                            }}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          ></iframe>
                        ) : (tutorial.steps || []).length > 0 ? (
                          <button type="button" onClick={() => setSelectedTutorial(tutorial)} data-testid={`tutorial-open-guide-${tutorial.id}`}
                            style={{ width: '100%', aspectRatio: '16/9', border: 'none', padding: 0, cursor: 'pointer', position: 'relative', background: '#f1f5f9' }}>
                            <img src={tutorial.steps[tutorial.steps.length > 5 ? 5 : 0].image} alt={tutorial.title}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
                            <span style={{ position: 'absolute', left: '50%', bottom: '12px', transform: 'translateX(-50%)', background: '#0586ba', color: 'white',
                              padding: '6px 14px', borderRadius: '999px', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                              Open step-by-step guide ({tutorial.steps.length} steps)
                            </span>
                          </button>
                        ) : (
                          <div style={{
                            width: '100%',
                            aspectRatio: '16/9',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#e5e7eb'
                          }}>
                            <Play style={{ width: '48px', height: '48px', color: '#9ca3af' }} />
                          </div>
                        )}
                      </div>
                      
                      {/* Title and Info */}
                      <div style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' }}>
                          <h4 style={{ fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minWidth: '24px',
                              height: '24px',
                              padding: '0 6px',
                              borderRadius: '12px',
                              backgroundColor: '#fee2e2',
                              color: '#dc2626',
                              fontSize: '12px',
                              fontWeight: '700',
                              flexShrink: 0
                            }}>
                              {tutorial.order}
                            </span>
                            <span style={{ lineHeight: '1.3' }}>{tutorial.title}</span>
                          </h4>
                        </div>
                        
                        {tutorial.description && (
                          <p style={{ 
                            fontSize: '12px', 
                            color: '#6b7280',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            marginBottom: '8px'
                          }}>
                            {tutorial.description}
                          </p>
                        )}
                        
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280' }}>
                          <span style={{
                            padding: '2px 8px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '4px',
                            fontSize: '11px',
                            textTransform: 'capitalize'
                          }}>
                            {tutorial.category}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(
    modalContent,
    document.body
  );
};

export default TutorialsModalPortal;
